import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { getAppScheme } from '../lib/app-scheme';
import {
  listLinkedAccounts,
  startSocialAccountLink,
  unlinkSocialAccount,
  type LinkableSocialProvider,
} from '../lib/auth-api';
import { confirm } from '../lib/confirm-action';
import { openSocialLinkFlow } from '../lib/social-link-navigation';
import { isAppAuthRequiredError } from '../lib/app-auth';
import { AUTH_LINKED_ACCOUNTS_QUERY_KEY, useAuth } from './useAuth';
import { useToast } from './useToast';

export type SignInMethodProvider = LinkableSocialProvider;

/**
 * Better Auth's linking refusals, in the words of someone who just tapped a
 * button. The codes arrive on the callback URL as `?error=<code>`.
 */
const LINK_ERROR_COPY_KEYS: Record<string, string> = {
  "email_doesn't_match": 'settings.signInMethods.errors.emailMismatch',
  email_not_found: 'settings.signInMethods.errors.emailMissing',
  account_already_linked_to_different_user:
    'settings.signInMethods.errors.alreadyLinkedElsewhere',
  unable_to_link_account: 'settings.signInMethods.errors.generic',
  account_not_linked: 'settings.signInMethods.errors.generic',
};

/**
 * Backs the "Sign-in methods" section in Settings › Account › Account controls.
 *
 * Runs its own linked-accounts query rather than leaning on `useAuth()`'s:
 * that one is disabled for UAE PASS-verified users (it exists only to decide
 * whether a password bootstrap is needed), which would show every method as
 * disconnected here. Same query key, so the two share one cache entry.
 */
export function useSignInMethods() {
  const { user, isAuthenticated, isTokenReady } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');
  const toast = useToast();
  const router = useRouter();
  const params = useLocalSearchParams<{ error?: string }>();
  const reportedErrorRef = useRef<string | null>(null);
  const [pendingProvider, setPendingProvider] =
    useState<SignInMethodProvider | null>(null);

  const query = useQuery({
    queryKey: [...AUTH_LINKED_ACCOUNTS_QUERY_KEY, user?.id ?? 'guest'],
    queryFn: listLinkedAccounts,
    enabled: isAuthenticated && isTokenReady && !!user?.id,
    staleTime: 1000 * 60,
    retry: (failureCount, error) =>
      !isAppAuthRequiredError(error) && failureCount < 2,
  });

  const accounts = useMemo(() => query.data ?? [], [query.data]);
  const hasPassword = accounts.some(
    (account) => account.providerId === 'credential',
  );

  const describeLinkError = useCallback(
    (code: string) =>
      t(
        LINK_ERROR_COPY_KEYS[code] ?? 'settings.signInMethods.errors.generic',
      ),
    [t],
  );

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: AUTH_LINKED_ACCOUNTS_QUERY_KEY,
      }),
    [queryClient],
  );

  // Web completes the hop as a full page navigation, so the outcome arrives as
  // a query parameter rather than a resolved promise. Report it once, then
  // strip it so a refresh does not resurface the message.
  useEffect(() => {
    const code = typeof params.error === 'string' ? params.error : null;
    if (!code || reportedErrorRef.current === code) return;

    reportedErrorRef.current = code;
    toast.error(describeLinkError(code));
    router.setParams({ error: undefined });
  }, [params.error, describeLinkError, router, toast]);

  const connect = useCallback(
    async (provider: SignInMethodProvider) => {
      if (pendingProvider) return;
      setPendingProvider(provider);

      try {
        const isNative = Platform.OS !== 'web';
        const { authorizeUrl, callbackURL } = await startSocialAccountLink({
          provider,
          clientType: isNative ? 'native' : 'web',
          ...(isNative ? { nativeScheme: getAppScheme() } : {}),
        });

        const outcome = await openSocialLinkFlow({
          authorizeUrl,
          returnUrl: callbackURL,
        });

        if (outcome.status === 'error') {
          toast.error(describeLinkError(outcome.code));
          return;
        }

        if (outcome.status === 'linked') {
          await invalidate();
          toast.success(
            t('settings.signInMethods.connectedToast', {
              provider: t(`settings.signInMethods.providers.${provider}`),
            }),
          );
        }
      } catch (error: any) {
        toast.error(
          error?.message ?? t('settings.signInMethods.errors.generic'),
        );
      } finally {
        setPendingProvider(null);
      }
    },
    [describeLinkError, invalidate, pendingProvider, t, toast],
  );

  const disconnect = useCallback(
    async (provider: SignInMethodProvider) => {
      if (pendingProvider) return;

      const account = accounts.find((item) => item.providerId === provider);
      if (!account) return;

      const providerLabel = t(`settings.signInMethods.providers.${provider}`);
      const confirmed = await confirm({
        title: t('settings.signInMethods.disconnectTitle', {
          provider: providerLabel,
        }),
        message: t('settings.signInMethods.disconnectMessage', {
          provider: providerLabel,
        }),
        confirmLabel: t('settings.signInMethods.disconnect'),
        cancelLabel: t('actions.cancel'),
        destructive: true,
      });

      if (!confirmed) return;

      setPendingProvider(provider);

      try {
        await unlinkSocialAccount({
          providerId: provider,
          accountId: account.accountId,
        });
        await invalidate();
        toast.success(
          t('settings.signInMethods.disconnectedToast', {
            provider: providerLabel,
          }),
        );
      } catch (error: any) {
        toast.error(
          error?.message ?? t('settings.signInMethods.errors.disconnect'),
        );
      } finally {
        setPendingProvider(null);
      }
    },
    [accounts, invalidate, pendingProvider, t, toast],
  );

  return {
    /** iOS and web offer Apple sign-in; Android does not, so neither does this. */
    providers: (Platform.OS === 'android'
      ? (['google'] as const)
      : (['google', 'apple'] as const)) as readonly SignInMethodProvider[],
    isConnected: (provider: SignInMethodProvider) =>
      accounts.some((account) => account.providerId === provider),
    hasPassword,
    /**
     * Better Auth refuses to remove the last account on a row, so do not offer
     * the action that is going to be rejected.
     */
    canDisconnect: accounts.length > 1,
    isLoading: query.isPending,
    pendingProvider,
    connect,
    disconnect,
  };
}
